import mergeImages from "merge-images"

import { prepareLayers as prepareFrontViewLayers } from "../helpers-shared/layers/frontViewLayers"
import { prepareLayers as prepareOutsideViewLayers } from "../helpers-shared/layers/outsideViewLayers"
import { prepareLayers as preparePanelViewLayers } from "../helpers-shared/layers/panelViewLayers"
import { getDataAssetsUrl } from "../helpers/general"

async function loadCropConfig(model) {
  try {
    const configsUrl = getDataAssetsUrl()
    const url = `${configsUrl}/configs/ingeteamev/${model.toLowerCase()}/image-crop.json`
    const response = await fetch(url)
    if (!response.ok) return null // Si no existe el archivo, no hay config
    const text = await response.text()
    if (!text) return null // Archivo vacío también lo consideramos no config
    return JSON.parse(text)
  } catch (error) {
    return null
  }
}

function shouldCrop(configuration, cropConfig) {
  for (const configEntry of cropConfig) {
    const conditionsMet = configEntry.CONDICIONES.every(
      cond => configuration[cond.ID_PROPIEDAD] === cond.ID_VALOR
    )
    if (!conditionsMet) continue

    const noRecortar = configEntry.NO_RECORTAR.some(
      cond => configuration[cond.ID_PROPIEDAD] === cond.ID_VALOR
    )
    return !noRecortar
  }
  return false
}

function getLayersByView(currentView, values) {
  switch (currentView) {
    case "PANEL":
      return preparePanelViewLayers(values)
    case "EXTERIOR":
      return prepareOutsideViewLayers(values)
    case "FRONTAL":
    default:
      return prepareFrontViewLayers(values)
  }
}

function buildResolvedSources(
  layers,
  assetsUrl,
  imageFolder,
  model,
  currentView,
  size
) {
  return layers
    .filter(Boolean)
    .map(({ folder, imageName, overrideRootFolder }) => {
      let rootFolder = `${assetsUrl}/${imageFolder}/${model.toUpperCase()}/${currentView}`
      if (overrideRootFolder != null) {
        const cleanOverrideRootFolder =
          overrideRootFolder === "/" ? "" : overrideRootFolder
        rootFolder = `${assetsUrl}/${imageFolder}${cleanOverrideRootFolder}`
      }
      return `${rootFolder}/${folder}/${size}/${imageName}.webp`
    })
}

async function createCroppedImage(base64, cropBottomPx, maxWidth, maxHeight) {
  const image = new Image()
  image.src = base64

  await new Promise(resolve => {
    image.onload = resolve
    image.onerror = e => {
      console.error("Error al cargar imagen base64 en objeto Image:", e)
      resolve()
    }
  })

  const realWidth = image.width
  const realHeight = image.height

  const scaleX = maxWidth / realWidth
  const scaleY = maxHeight / (realHeight - cropBottomPx)
  const scale = Math.min(scaleX, scaleY)

  const canvas = document.createElement("canvas")
  canvas.width = realWidth * scale
  canvas.height = (realHeight - cropBottomPx) * scale

  const ctx = canvas.getContext("2d")
  ctx.drawImage(
    image,
    0,
    0,
    realWidth,
    realHeight - cropBottomPx,
    0,
    0,
    canvas.width,
    canvas.height
  )

  return canvas.toDataURL("image/png")
}

export const useMergedViewImage = ({
  model,
  currentView,
  imageFolder,
  originalSize,
  sizeFolder,
}) => {
  const assetsUrl = getDataAssetsUrl()
  const size = sizeFolder ?? "S"
  const generateMergedImage = async values => {
    if (!values) return null

    const layers = getLayersByView(currentView, values)
    const resolvedSources = buildResolvedSources(
      layers,
      assetsUrl,
      imageFolder,
      model,
      currentView,
      size
    )

    if (!resolvedSources.length) return null

    try {
      const cropConfig = await loadCropConfig(model)
      if (!cropConfig) {
        return await mergeImages(resolvedSources)
      }

      const recortar = shouldCrop(values, cropConfig)
      const fullImageBase64 = await mergeImages(resolvedSources)

      if (!recortar || originalSize) return fullImageBase64

      const cropBottomPx = 200
      const maxWidth = 887
      const maxHeight = 400

      return await createCroppedImage(
        fullImageBase64,
        cropBottomPx,
        maxWidth,
        maxHeight
      )
    } catch (err) {
      console.error("Error generando imagen combinada:", err)
      return null
    }
  }

  return { generateMergedImage }
}
